// Lot 2b — Radar de force : tables de référence et calcul des cibles de bloc.
// Module pur (aucun accès réseau, aucun état) — toute la logique est testable à la main
// depuis les exemples chiffrés de `systeme_cible.calcul_cible_bloc` du fichier de standards.
//
// ⚠️ VOCABULAIRE — règle produit impérative (cf. brief lot 2b et
// `systeme_cible.radar_affichage.regle_imperative` du fichier de standards) :
// les mots « Débutant / Intermédiaire / Avancé » désignent DÉJÀ le niveau de programme
// du client (`wizard_submissions.level_id`, qui change tous les 12 semaines). Les paliers
// des tables ExRx sont une notion différente et ne doivent jamais porter ces trois mots,
// ni dans l'interface ni dans le code. Le fichier JSON source, lui, nomme ses colonnes
// `debutant`/`intermediaire`/`avance` : cette correspondance est confinée à `TIER_JSON_KEY`
// ci-dessous, et tout le reste de l'app manipule les noms ExRx (`novice`/`intermediate`/
// `advanced`). Ne pas réintroduire les clés françaises au-delà de cette frontière.

import standards from "@/data/strength_standards.json"

// ── Types ───────────────────────────────────────────────────

export type StrengthMovement =
  | "squat"
  | "souleve_de_terre"
  | "developpe_couche"
  | "developpe_militaire"
  | "traction_lestee"

export type StandardsGender = "homme" | "femme"
export type AgeBracket = "18-39" | "40-49" | "50-59"

/** Paliers des tables ExRx. Volontairement nommés à l'anglaise — voir l'avertissement en tête de fichier. */
export type StandardTier = "novice" | "intermediate" | "advanced"

export type TargetZone =
  | "sous_palier_novice"
  | "entre_novice_et_intermediate"
  | "au_dela_intermediate"

export type BeginnerExperience = "vrai_debutant" | "reprise_recente" | "reprise_ancienne"

// ── Constantes dérivées du fichier de standards ─────────────

/** Seule frontière autorisée entre les noms de colonnes du JSON et le vocabulaire de l'app. */
const TIER_JSON_KEY: Record<StandardTier, "debutant" | "intermediaire" | "avance"> = {
  novice: "debutant",
  intermediate: "intermediaire",
  advanced: "avance",
}

export const STRENGTH_MOVEMENTS: StrengthMovement[] = [
  "squat",
  "souleve_de_terre",
  "developpe_couche",
  "developpe_militaire",
  "traction_lestee",
]

/** Libellés FR affichés sur le radar. Aucun ne contient de nom de palier — c'est voulu. */
export const MOVEMENT_LABELS: Record<StrengthMovement, string> = {
  squat: "Squat",
  souleve_de_terre: "Soulevé de terre",
  developpe_couche: "Développé couché",
  developpe_militaire: "Développé militaire",
  traction_lestee: "Traction lestée",
}

/** Libellé court, pour les sommets du radar où la place manque. */
export const MOVEMENT_SHORT_LABELS: Record<StrengthMovement, string> = {
  squat: "Squat",
  souleve_de_terre: "Soulevé",
  developpe_couche: "Couché",
  developpe_militaire: "Militaire",
  traction_lestee: "Traction",
}

/**
 * Colonne 1RM d'`athlete_profiles` correspondant à chaque mouvement des tables.
 * `rm_pull_up` est une charge TOTALE (corps + lest), pas un lest — cf. `pullUpRatioFromTotal`.
 */
export const RM_FIELD_BY_MOVEMENT: Record<StrengthMovement, string> = {
  squat: "rm_back_squat",
  souleve_de_terre: "rm_deadlift",
  developpe_couche: "rm_bench_press",
  developpe_militaire: "rm_overhead_press",
  traction_lestee: "rm_pull_up",
}

/**
 * Bas du corps vs haut du corps — repris tel quel de
 * `systeme_cible.zone_par_mouvement.zones.sous_palier_novice.mouvements_bas_du_corps`.
 * La traction est classée « bas du corps » par le fichier de référence (elle suit donc
 * les pourcentages 20/8/4). Ce n'est pas une coquille : ne pas la « corriger ».
 */
const LOWER_BODY_MOVEMENTS = new Set<StrengthMovement>([
  "squat",
  "souleve_de_terre",
  "traction_lestee",
])

/** Pourcentages de progression par zone, en dur depuis le fichier de standards. */
const ZONE_PERCENTS: Record<TargetZone, { lower: number; upper: number }> = {
  sous_palier_novice: { lower: 0.2, upper: 0.15 },
  entre_novice_et_intermediate: { lower: 0.08, upper: 0.06 },
  au_dela_intermediate: { lower: 0.04, upper: 0.03 },
}

/**
 * Fraction du pourcentage de zone qu'il faut avoir réellement parcourue au test
 * intermédiaire pour déclencher le passage à la cible ajustée : la moitié du chemin.
 *
 * Remplace un seuil fixe à 90 % de la cible (`mesure >= 0,90 × cible`). Ce seuil-là
 * était piégeux : avec `cible = valeur_initiale × (1 + pct)`, il se réduisait à
 * `mesure/valeur_initiale >= 0,90 × (1 + pct)`, soit un seuil INFÉRIEUR à la valeur de
 * départ pour tout pct <= 11,1 %. Les zones à 8/6/4/3 % déclenchaient donc l'ajustement
 * même quand le client avait stagné, voire régressé (jusqu'à −2,8 % en zone 8 %). Le
 * seuil est désormais exprimé en progression réelle depuis le test initial, ce qui le
 * rend cohérent quelle que soit la zone.
 */
export const MID_BLOCK_PROGRESS_FRACTION = 0.5

/** Multiplicateur appliqué au pourcentage de zone quand le déclencheur est atteint. */
export const MID_BLOCK_PERCENT_MULTIPLIER = 1.5

// ── Genre et tranche d'âge ──────────────────────────────────

export function toStandardsGender(gender: "male" | "female"): StandardsGender {
  return gender === "male" ? "homme" : "femme"
}

export const GENDER_SOURCE_LABEL: Record<StandardsGender, string> = {
  homme: "Homme",
  femme: "Femme",
}

export interface AgeBracketResolution {
  bracket: AgeBracket
  /** true quand l'âge réel sort des tranches couvertes et qu'on a rabattu sur une borne. */
  clamped: boolean
  ageYears: number | null
}

/** Âge révolu à partir d'une date de naissance ISO (`YYYY-MM-DD`). */
export function ageFromBirthDate(birthDate: string | null | undefined, today: Date = new Date()): number | null {
  if (!birthDate) return null
  const [y, m, d] = birthDate.split("-").map(Number)
  if (!y || !m || !d) return null
  let age = today.getFullYear() - y
  const beforeBirthdayThisYear =
    today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)
  if (beforeBirthdayThisYear) age -= 1
  return age >= 0 && age < 120 ? age : null
}

/**
 * Tranche d'âge des tables. Le fichier de standards ne couvre que 18-39 / 40-49 / 50-59 :
 * au-delà de 59 ans on rabat sur 50-59 (consigne du brief : jamais d'extrapolation, jamais
 * de plantage) et on le signale dans les logs — ce cas mérite un vrai calibrage plus tard.
 * En dessous de 18 ans, même principe avec la tranche basse.
 */
export function resolveAgeBracket(
  birthDate: string | null | undefined,
  fallbackAge: number | null | undefined,
  today: Date = new Date()
): AgeBracketResolution {
  const age = ageFromBirthDate(birthDate, today) ?? (typeof fallbackAge === "number" ? fallbackAge : null)

  if (age == null) {
    // Ni date de naissance ni âge : la tranche la plus large est le repli le moins faux.
    console.warn("[strengthStandards] âge inconnu — repli sur la tranche 18-39")
    return { bracket: "18-39", clamped: true, ageYears: null }
  }
  if (age >= 60) {
    console.warn(
      `[strengthStandards] âge ${age} ans hors tables (max 59) — repli sur la tranche 50-59, ` +
        "cibles à recalibrer quand les tranches 60+ seront disponibles"
    )
    return { bracket: "50-59", clamped: true, ageYears: age }
  }
  if (age < 18) {
    console.warn(`[strengthStandards] âge ${age} ans hors tables (min 18) — repli sur la tranche 18-39`)
    return { bracket: "18-39", clamped: true, ageYears: age }
  }
  if (age >= 50) return { bracket: "50-59", clamped: false, ageYears: age }
  if (age >= 40) return { bracket: "40-49", clamped: false, ageYears: age }
  return { bracket: "18-39", clamped: false, ageYears: age }
}

// ── Lecture des tables ──────────────────────────────────────

type BarbellTable = Record<string, Record<string, number>>

function isBarbellMovement(movement: StrengthMovement): boolean {
  return movement !== "traction_lestee"
}

function barbellTable(
  movement: StrengthMovement,
  gender: StandardsGender,
  bracket: AgeBracket
): BarbellTable | null {
  const mv = (standards.mouvements as Record<string, unknown>)[movement] as
    | Record<string, unknown>
    | undefined
  if (!mv) return null
  const byBracket = mv[gender] as Record<string, BarbellTable> | undefined
  return byBracket?.[bracket] ?? null
}

/**
 * Palier interpolé au poids de corps exact.
 *
 * Interpolation LINÉAIRE entre les deux lignes de poids encadrantes — jamais d'arrondi à
 * la ligne la plus proche (consigne explicite du brief). Hors bornes, on prend la ligne
 * extrême sans extrapoler, conformément à la clé `interpolation` du fichier de standards.
 *
 * Pour la traction lestée, la table ne dépend pas du poids de corps : elle donne
 * directement un ratio (charge totale / poids de corps), renvoyé tel quel.
 */
export function standardForBodyweight(
  movement: StrengthMovement,
  gender: StandardsGender,
  bracket: AgeBracket,
  bodyweightKg: number,
  tier: StandardTier
): number | null {
  const tierKey = TIER_JSON_KEY[tier]

  if (!isBarbellMovement(movement)) {
    const mv = standards.mouvements.traction_lestee as unknown as Record<
      string,
      Record<string, Record<string, number>>
    >
    const value = mv[gender]?.[bracket]?.[tierKey]
    return typeof value === "number" ? value : null
  }

  const table = barbellTable(movement, gender, bracket)
  if (!table) return null
  if (!Number.isFinite(bodyweightKg) || bodyweightKg <= 0) return null

  const weights = Object.keys(table)
    .map(Number)
    .filter((w) => Number.isFinite(w))
    .sort((a, b) => a - b)
  if (weights.length === 0) return null

  const first = weights[0]
  const last = weights[weights.length - 1]
  if (bodyweightKg <= first) return table[String(first)][tierKey] ?? null
  if (bodyweightKg >= last) return table[String(last)][tierKey] ?? null

  let lower = first
  for (const w of weights) {
    if (w <= bodyweightKg) lower = w
    else break
  }
  const upper = weights.find((w) => w > bodyweightKg)
  if (upper == null) return table[String(lower)][tierKey] ?? null

  const lowValue = table[String(lower)][tierKey]
  const highValue = table[String(upper)][tierKey]
  if (typeof lowValue !== "number" || typeof highValue !== "number") return null
  if (upper === lower) return lowValue

  const ratio = (bodyweightKg - lower) / (upper - lower)
  return lowValue + (highValue - lowValue) * ratio
}

// ── Traction lestée : lest ↔ ratio ──────────────────────────

/**
 * Ratio de charge totale d'une traction : charge totale / poids de corps.
 *
 * ⚠️ SÉMANTIQUE DE `athlete_profiles.rm_pull_up` : cette colonne stocke le **poids total
 * du système** (poids de corps + lest), PAS le lest seul. C'est le contrat établi par
 * `exerciseNormalization.ts` — l'entrée `"Pull-Up": { base: "rm_pull_up", ratio: 100,
 * bw: true }` y est commentée « le résultat est le poids total du système (poids de corps
 * + lest) », et le calcul de charge en fait `diff = total - bodyweightKg` pour afficher
 * « ~N kg de lest ». Le ratio est donc `rm_pull_up / poids de corps`, sans ré-ajouter le
 * poids de corps. Une valeur sous le poids de corps (traction assistée) donne bien un
 * ratio sous 1,00, conforme à la clé `definition` du fichier de standards.
 *
 * Ne pas se fier au libellé de l'interface pour deviner cette sémantique : c'est
 * exactement ce qui a produit le bug de double comptage du poids de corps (un 1RM de
 * 81 kg à 72 kg de corps donnait un ratio de 2,13 au lieu de 1,13).
 */
export function pullUpRatioFromTotal(totalLoadKg: number, bodyweightKg: number): number | null {
  if (!Number.isFinite(bodyweightKg) || bodyweightKg <= 0) return null
  if (!Number.isFinite(totalLoadKg) || totalLoadKg <= 0) return null
  return totalLoadKg / bodyweightKg
}

/** Charge totale en kg que représente un ratio — même unité que `rm_pull_up`. */
export function pullUpTotalFromRatio(ratio: number, bodyweightKg: number): number | null {
  if (!Number.isFinite(ratio) || !Number.isFinite(bodyweightKg) || bodyweightKg <= 0) return null
  return ratio * bodyweightKg
}

/**
 * Lest en kg que représente un ratio — la part ajoutée au-dessus du poids de corps.
 * Négatif = assistance. Sert uniquement à l'affichage à côté de la charge totale.
 */
export function pullUpAddedLoadFromRatio(ratio: number, bodyweightKg: number): number | null {
  const total = pullUpTotalFromRatio(ratio, bodyweightKg)
  return total == null ? null : total - bodyweightKg
}

/**
 * Valeur comparable aux tables pour un mouvement donné : le 1RM en kg pour les mouvements
 * à la barre, le ratio de charge totale pour la traction (dont le 1RM stocké est déjà une
 * charge totale — voir `pullUpRatioFromTotal`).
 */
export function comparableValue(
  movement: StrengthMovement,
  rawRmValue: number | null | undefined,
  bodyweightKg: number
): number | null {
  if (typeof rawRmValue !== "number" || !Number.isFinite(rawRmValue)) return null
  if (movement === "traction_lestee") return pullUpRatioFromTotal(rawRmValue, bodyweightKg)
  return rawRmValue > 0 ? rawRmValue : null
}

// ── Zone et pourcentage ─────────────────────────────────────

/**
 * Zone du client SUR CE MOUVEMENT. Volontairement indépendante du niveau de programme
 * déclaré à l'inscription : un même client peut être sous le palier Novice au développé
 * militaire et au-delà du palier Intermediate au soulevé de terre.
 */
export function determineZone(
  currentValue: number,
  noviceStandard: number,
  intermediateStandard: number
): TargetZone {
  if (currentValue < noviceStandard) return "sous_palier_novice"
  if (currentValue < intermediateStandard) return "entre_novice_et_intermediate"
  return "au_dela_intermediate"
}

const ZONE_ORDER: TargetZone[] = [
  "sous_palier_novice",
  "entre_novice_et_intermediate",
  "au_dela_intermediate",
]

/**
 * Remontée d'un cran de la zone, pour le client qui a déclaré `reprise_recente` au
 * questionnaire d'expérience (mémoire musculaire + récupération nerveuse rapide).
 * `vrai_debutant` et `reprise_ancienne` ne décalent rien.
 */
export function applyBeginnerExperience(zone: TargetZone, experience: BeginnerExperience | null): TargetZone {
  if (experience !== "reprise_recente") return zone
  const i = ZONE_ORDER.indexOf(zone)
  return ZONE_ORDER[Math.min(i + 1, ZONE_ORDER.length - 1)]
}

export function zonePercent(movement: StrengthMovement, zone: TargetZone): number {
  const percents = ZONE_PERCENTS[zone]
  return LOWER_BODY_MOVEMENTS.has(movement) ? percents.lower : percents.upper
}

// ── Calcul de la cible de bloc ──────────────────────────────

export interface BlockTargetComputation {
  movement: StrengthMovement
  /** kg pour les mouvements à la barre, ratio de charge totale pour la traction. */
  initialValue: number
  zone: TargetZone
  /** Zone avant l'éventuelle remontée liée au questionnaire d'expérience. */
  naturalZone: TargetZone
  percent: number
  targetValue: number
  noviceStandard: number
  intermediateStandard: number
}

/**
 * Cible du bloc pour un mouvement : `cible = valeur_test_initial × (1 + pct_de_la_zone)`.
 * Renvoie null si le 1RM n'est pas renseigné ou si les tables ne couvrent pas le cas.
 */
export function computeBlockTarget(params: {
  movement: StrengthMovement
  gender: StandardsGender
  bracket: AgeBracket
  bodyweightKg: number
  /** Valeur brute d'`athlete_profiles` : kg à la barre, charge TOTALE en kg pour la traction. */
  rawRmValue: number | null | undefined
  beginnerExperience?: BeginnerExperience | null
}): BlockTargetComputation | null {
  const { movement, gender, bracket, bodyweightKg, rawRmValue, beginnerExperience } = params

  const initialValue = comparableValue(movement, rawRmValue, bodyweightKg)
  if (initialValue == null || initialValue <= 0) return null

  const noviceStandard = standardForBodyweight(movement, gender, bracket, bodyweightKg, "novice")
  const intermediateStandard = standardForBodyweight(movement, gender, bracket, bodyweightKg, "intermediate")
  if (noviceStandard == null || intermediateStandard == null) return null

  const naturalZone = determineZone(initialValue, noviceStandard, intermediateStandard)
  const zone = applyBeginnerExperience(naturalZone, beginnerExperience ?? null)
  const percent = zonePercent(movement, zone)

  return {
    movement,
    initialValue,
    zone,
    naturalZone,
    percent,
    targetValue: initialValue * (1 + percent),
    noviceStandard,
    intermediateStandard,
  }
}

/**
 * Seuil de déclenchement de l'ajustement au test intermédiaire, dans l'unité de la
 * valeur mesurée : il faut avoir parcouru au moins la moitié du chemin vers la cible
 * initiale. `seuil = valeur_test_initial × (1 + pct / 2)`.
 */
export function midBlockTriggerThreshold(initialValue: number, initialPercent: number): number {
  return initialValue * (1 + initialPercent * MID_BLOCK_PROGRESS_FRACTION)
}

/**
 * Tolérance relative sur la comparaison au seuil.
 *
 * `100 × (1 + 0,20 × 0,5)` vaut 110.00000000000001 en IEEE 754, pas 110 : sans tolérance,
 * un client passant de 100 à 110 kg — pile la moitié du chemin, et un couple de valeurs on
 * ne peut plus plausible en kilos ronds — ne déclencherait PAS l'ajustement, par pur artefact
 * binaire. Même chose en zone 3 % dans l'autre sens. On compare donc à epsilon près.
 */
const MID_BLOCK_THRESHOLD_EPSILON = 1e-9

/**
 * Ajustement au test intermédiaire.
 *
 * Déclencheur : `mesure >= valeur_test_initial × (1 + pct / 2)` — la moitié du chemin
 * vers la cible initiale doit être réellement parcourue. Un client qui a stagné ou
 * régressé ne déclenche donc rien, quelle que soit sa zone (cf.
 * `MID_BLOCK_PROGRESS_FRACTION` pour le seuil fixe à 90 % qu'il remplace).
 *
 * ⚠️ Le recalcul part TOUJOURS de la valeur du test initial, jamais de la valeur mesurée
 * au test intermédiaire : cumuler sur la mesure intermédiaire ferait dériver la cible de
 * façon incontrôlée à chaque recalcul. `nouvelle_cible = valeur_test_initial × (1 + pct × 1,5)`.
 *
 * `currentTarget` ne sert plus qu'à renvoyer la cible inchangée quand le seuil n'est pas
 * atteint : il n'entre plus dans le déclencheur.
 */
export function adjustTargetAtMidBlock(params: {
  initialValue: number
  initialPercent: number
  currentTarget: number
  measuredValue: number
}): { triggered: boolean; percent: number; targetValue: number } {
  const { initialValue, initialPercent, currentTarget, measuredValue } = params

  const threshold = midBlockTriggerThreshold(initialValue, initialPercent)
  const triggered = measuredValue >= threshold - Math.abs(threshold) * MID_BLOCK_THRESHOLD_EPSILON
  if (!triggered) {
    return { triggered: false, percent: initialPercent, targetValue: currentTarget }
  }
  const percent = initialPercent * MID_BLOCK_PERCENT_MULTIPLIER
  return { triggered: true, percent, targetValue: initialValue * (1 + percent) }
}

// ── Mention de source affichée sous le radar ────────────────

/**
 * Tranche de poids encadrante affichée dans la mention de source, ex. « 67–75 kg ».
 * Purement informatif : le calcul, lui, interpole au poids exact.
 */
export function bodyweightBandLabel(
  gender: StandardsGender,
  bracket: AgeBracket,
  bodyweightKg: number
): string | null {
  const table = barbellTable("squat", gender, bracket)
  if (!table) return null
  const weights = Object.keys(table)
    .map(Number)
    .sort((a, b) => a - b)
  if (weights.length === 0) return null

  const first = weights[0]
  const last = weights[weights.length - 1]
  if (bodyweightKg <= first) return `≤ ${first}`
  if (bodyweightKg >= last) return `≥ ${weights[weights.length - 2] ?? last}`

  let lower = first
  for (const w of weights) {
    if (w <= bodyweightKg) lower = w
    else break
  }
  const upper = weights.find((w) => w > bodyweightKg)
  return upper == null ? `${lower}` : `${lower}–${upper}`
}

/** Source de la table de traction — référence coach, pas ExRx. Affichée séparément. */
export const PULL_UP_SOURCE_NOTE =
  "La traction lestée suit une référence coach Zeyone (absente d'ExRx), calibrée sur le ton conservateur des tables."
